// src/store/slices/system/smsBalanceSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

// ===== LIST ALL BALANCES =====
export const getBalances = createAsyncThunk(
  'smsBalance/getBalances',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SMS_BALANCES);
      console.log("SMS BALANCES RESPPNSE: ", response);
      
      const { status, message, data } = response.data;

      if (status === 200) {
        // ✅ Extract the array from data.data
        const list = data?.data || [];
        return {
          success: true,
          message: message || 'Balances loaded',
          data: list, // now an array
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to load balances',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Get balances error:', error);
      let errorMessage = 'Failed to load balances';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to load balances';
        if (error.response.data?.errors) {
          const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== GET SINGLE BALANCE =====
export const getBalance = createAsyncThunk(
  'smsBalance/getBalance',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SMS_BALANCE_SHOW(id));
      console.log("SMS BALANCE RESPPNSE: ", response);

      const { status, message, balance } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Balance retrieved',
          balance: balance || 0,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to retrieve balance',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Get balance error:', error);
      let errorMessage = 'Failed to retrieve balance';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to retrieve balance';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== ADD BALANCE =====
export const addBalance = createAsyncThunk(
  'smsBalance/addBalance',
  async ({ id, amount, reason }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SMS_BALANCE_ADD(id), { amount, reason });
      const { status, message, balance } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Balance added',
          balance: balance || 0,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to add balance',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Add balance error:', error);
      let errorMessage = 'Failed to add balance';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to add balance';
        if (error.response.data?.errors) {
          const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== DEDUCT BALANCE =====
export const deductBalance = createAsyncThunk(
  'smsBalance/deductBalance',
  async ({ id, amount, reason }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SMS_BALANCE_DEDUCT(id), { amount, reason });
      const { status, message, balance } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Balance deducted',
          balance: balance || 0,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to deduct balance',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Deduct balance error:', error);
      let errorMessage = 'Failed to deduct balance';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to deduct balance';
        if (error.response.data?.errors) {
          const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== SET BALANCE =====
export const setBalance = createAsyncThunk(
  'smsBalance/setBalance',
  async ({ id, balance, reason }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.SMS_BALANCE_SET(id), { balance, reason });
      const { status, message, balance: newBalance } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Balance set',
          balance: newBalance || 0,
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to set balance',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Set balance error:', error);
      let errorMessage = 'Failed to set balance';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to set balance';
        if (error.response.data?.errors) {
          const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// ===== GET TRANSACTIONS =====
export const getTransactions = createAsyncThunk(
  'smsBalance/getTransactions',
  async ({ id, page = 1, perPage = 20 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.SMS_BALANCE_TRANSACTIONS(id), {
        params: { page, per_page: perPage }
      });
      const { status, message, data } = response.data;

      if (status === 200) {
        // data is { data: [...], meta: {...} }
        return {
          success: true,
          message: message || 'Transactions loaded',
          data: {
            data: data?.data || [],
            meta: data?.meta || {},
          },
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to load transactions',
          status,
          apiResponse: response.data,
        });
      }
    } catch (error) {
      console.error('Get transactions error:', error);
      let errorMessage = 'Failed to load transactions';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        errorMessage = error.response.data?.message || 'Failed to load transactions';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data,
      });
    }
  }
);

// =============================================
// SLICE
// =============================================

const initialState = {
  balances: [],
  currentBalance: null,
  transactions: [],
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  },
  isLoading: false,
  isSubmitting: false,
  error: null,
  successMessage: null,
  apiMessage: null,
};

const smsBalanceSlice = createSlice({
  name: 'smsBalance',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearApiMessage: (state) => {
      state.apiMessage = null;
    },
    clearSmsBalanceState: (state) => {
      state.balances = [];
      state.currentBalance = null;
      state.transactions = [];
      state.pagination = initialState.pagination;
      state.isLoading = false;
      state.isSubmitting = false;
      state.error = null;
      state.successMessage = null;
      state.apiMessage = null;
    },
    clearCurrentBalance: (state) => {
      state.currentBalance = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== GET BALANCES =====
      .addCase(getBalances.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(getBalances.fulfilled, (state, action) => {
        state.isLoading = false;
        // action.payload.data is now the array
        state.balances = action.payload.data || [];
        state.successMessage = action.payload.message || 'Balances loaded';
        state.apiMessage = action.payload.message || 'Balances loaded';
        state.error = null;
      })
      .addCase(getBalances.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to load balances';
        state.apiMessage = action.payload?.message || 'Failed to load balances';
        state.successMessage = null;
        state.balances = [];
      })

      // ===== GET BALANCE =====
      .addCase(getBalance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(getBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBalance = action.payload.balance || 0;
        state.successMessage = action.payload.message || 'Balance retrieved';
        state.apiMessage = action.payload.message || 'Balance retrieved';
        state.error = null;
      })
      .addCase(getBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to retrieve balance';
        state.apiMessage = action.payload?.message || 'Failed to retrieve balance';
        state.successMessage = null;
        state.currentBalance = null;
      })

      // ===== ADD BALANCE =====
      .addCase(addBalance.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(addBalance.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Balance added';
        state.apiMessage = action.payload.message || 'Balance added';
        state.error = null;
        if (state.balances.length > 0) {
          const idx = state.balances.findIndex(b => b.id === action.meta.arg.id);
          if (idx !== -1) {
            state.balances[idx].sms_balance = action.payload.balance;
          }
        }
      })
      .addCase(addBalance.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to add balance';
        state.apiMessage = action.payload?.message || 'Failed to add balance';
        state.successMessage = null;
      })

      // ===== DEDUCT BALANCE =====
      .addCase(deductBalance.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(deductBalance.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Balance deducted';
        state.apiMessage = action.payload.message || 'Balance deducted';
        state.error = null;
        if (state.balances.length > 0) {
          const idx = state.balances.findIndex(b => b.id === action.meta.arg.id);
          if (idx !== -1) {
            state.balances[idx].sms_balance = action.payload.balance;
          }
        }
      })
      .addCase(deductBalance.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to deduct balance';
        state.apiMessage = action.payload?.message || 'Failed to deduct balance';
        state.successMessage = null;
      })

      // ===== SET BALANCE =====
      .addCase(setBalance.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(setBalance.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message || 'Balance set';
        state.apiMessage = action.payload.message || 'Balance set';
        state.error = null;
        if (state.balances.length > 0) {
          const idx = state.balances.findIndex(b => b.id === action.meta.arg.id);
          if (idx !== -1) {
            state.balances[idx].sms_balance = action.payload.balance;
          }
        }
      })
      .addCase(setBalance.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload?.message || 'Failed to set balance';
        state.apiMessage = action.payload?.message || 'Failed to set balance';
        state.successMessage = null;
      })

      // ===== GET TRANSACTIONS =====
      .addCase(getTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        const payloadData = action.payload.data || { data: [], meta: {} };
        state.transactions = payloadData.data || [];
        state.pagination = payloadData.meta || {
          current_page: 1,
          last_page: 1,
          per_page: 20,
          total: 0,
        };
        state.successMessage = action.payload.message || 'Transactions loaded';
        state.apiMessage = action.payload.message || 'Transactions loaded';
        state.error = null;
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to load transactions';
        state.apiMessage = action.payload?.message || 'Failed to load transactions';
        state.successMessage = null;
        state.transactions = [];
        state.pagination = {
          current_page: 1,
          last_page: 1,
          per_page: 20,
          total: 0,
        };
      });
  },
});

export const {
  clearError,
  clearSuccessMessage,
  clearApiMessage,
  clearSmsBalanceState,
  clearCurrentBalance,
} = smsBalanceSlice.actions;

// ===== SELECTORS – with guards =====
export const selectBalances = (state) => {
  if (state?.smsBalance && Array.isArray(state.smsBalance.balances)) {
    return state.smsBalance.balances;
  }
  return [];
};

export const selectCurrentBalance = (state) => {
  return state?.smsBalance?.currentBalance ?? null;
};

export const selectTransactions = (state) => {
  if (state?.smsBalance && Array.isArray(state.smsBalance.transactions)) {
    return state.smsBalance.transactions;
  }
  return [];
};

export const selectTransactionPagination = (state) => {
  if (state?.smsBalance?.pagination) {
    return state.smsBalance.pagination;
  }
  return {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  };
};

export const selectSmsBalanceLoading = (state) => state?.smsBalance?.isLoading || false;
export const selectSmsBalanceSubmitting = (state) => state?.smsBalance?.isSubmitting || false;
export const selectSmsBalanceApiMessage = (state) => state?.smsBalance?.apiMessage || null;
export const selectSmsBalanceError = (state) => state?.smsBalance?.error || null;
export const selectSmsBalanceSuccess = (state) => state?.smsBalance?.successMessage || null;

export default smsBalanceSlice.reducer;